import React, { FC } from 'react';
import { Input, InputGroup, InputGroupText, Table } from 'reactstrap';
import { Payment } from '../../shared/orders/model';
import { useTranslation } from '../translations';
import { sumBy, useConfig } from '../utils';
import { Currency } from './Currency';
import { DateTime } from './Date';
import { PreSubmitData } from './json-form/JSONForm';

interface RefundProps {
  readonly payments: Payment[];
  readonly totalToRefund?: number;
}

interface PaymentData {
  paymentData?: Record<string, number>;
}

export const RefundPaymentTable: FC<RefundProps> = ({ payments, totalToRefund }: RefundProps) => {
  const { ts } = useTranslation();
  const renderPayments = payments
    .filter(({ status }) => status === 'success')
    .map((payment) => {
      // Payments with the same reference can be a refund
      const relatedPayments = payments.filter(
        ({ reference, status }) =>
          reference === payment.reference && (status === 'success' || status === 'refunded'),
      );

      // If the payment already has been partially refunded, subtract it
      const remainingBalance = sumBy(relatedPayments, ({ amount, status }) =>
        status === 'success' ? amount : -amount,
      );

      return (
        <RefundPaymentTableItem
          key={payment.id}
          payment={payment}
          remainingBalance={remainingBalance}
          totalToRefund={totalToRefund}
        />
      );
    });

  return (
    <Table striped>
      <thead>
        <tr>
          <th>{ts('refund_payment_table.payment_date')}</th>
          <th>{ts('refund_payment_table.available_amount')}</th>
          <th>{ts('common.gateway')}</th>
          <th>{ts('refund_payment_table.amount_to_refund')}</th>
        </tr>
      </thead>
      <tbody>{renderPayments}</tbody>
    </Table>
  );
};

export const refundUpdatePreSubmit = ({ inputs }: PreSubmitData<PaymentData>): void => {
  if (!inputs.paymentData) {
    inputs.paymentData = {};
  }

  for (const [k, v] of Object.entries(inputs.paymentData)) {
    inputs.paymentData[k] = v * 100;
  }
};

interface RefundPaymentTableItemProps {
  readonly payment: Payment;
  readonly remainingBalance: number;
  readonly totalToRefund?: number;
}

const RefundPaymentTableItem: FC<RefundPaymentTableItemProps> = ({
  payment,
  remainingBalance,
  totalToRefund,
}) => {
  const { currencySymbol } = useConfig();
  let amountToRefund = remainingBalance;

  if (totalToRefund) {
    if (remainingBalance >= totalToRefund) {
      amountToRefund = totalToRefund;
    }

    totalToRefund -= amountToRefund;
  }

  return (
    <tr>
      <td>
        <DateTime value={payment.createdAt} />
      </td>
      <td>
        <Currency value={remainingBalance} />
      </td>
      <td>{payment.gateway}</td>
      <td>
        <InputGroup>
          <InputGroupText>{currencySymbol}</InputGroupText>
          <Input
            defaultValue={totalToRefund === undefined ? 0 : amountToRefund / 100}
            disabled={remainingBalance <= 0}
            id={`payment${payment.id}Amount`}
            min="0"
            name={`paymentData[${payment.id}]`}
            step="0.01"
            type="number"
          />
        </InputGroup>
      </td>
    </tr>
  );
};
